import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Checkbox,
  ListItem,
  List,
  Divider,
} from "@mui/material";
import { WebAssetResponse } from "@akitabox/api-client";
import { api } from "../api";

interface RecalculateLifeCycleDatesDialogProps {
  open: boolean;
  onClose: () => void;
  assets: WebAssetResponse[];
}

const RecalculateLifeCycleDatesDialog: React.FC<
  RecalculateLifeCycleDatesDialogProps
> = ({ open, onClose, assets }) => {
  const [recalculateAssessedDueDate, setRecalculateAssessedDueDate] =
    useState<boolean>(false);
  const [recalculateScheduledDueDate, setRecalculateScheduledDueDate] =
    useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [erroredAssets, setErroredAssets] = useState<WebAssetResponse[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [numTotalAssets, setNumTotalAssets] = useState<number>(0);
  const [numAssetsUpdated, setNumAssetsUpdated] = useState<number>(0);

  const updateDates = async (asset: WebAssetResponse) => {
    if (!asset.installation_date) {
      throw new Error("Missing installation date");
    }
    const installationDate = new Date(asset.installation_date);
    const res = await api.costLines.getByOrganization({
      organizationId: asset.organization,
      asset: asset._id,
    });
    const costLines = res.data;
    const replacementCostLines = costLines.filter(
      (costLine) => costLine.category === "replacement"
    );
    if (replacementCostLines.length === 0) {
      throw new Error("No replacement cost lines found");
    }
    for (const costLine of replacementCostLines) {
      const interval = costLine.user_interval ?? costLine.suggested_interval;
      if (!interval) continue;
      const updates: any = {};
      if (recalculateAssessedDueDate) {
        const assessedDueDate = costLine.assessed_due_date
          ? new Date(costLine.assessed_due_date)
          : null;
        const updatedYear = installationDate.getFullYear() + interval;
        if (!assessedDueDate || assessedDueDate.getFullYear() !== updatedYear) {
          const updatedDueDate = new Date(installationDate.getTime());
          updatedDueDate.setFullYear(updatedYear);
          updates.assessed_due_date = updatedDueDate.getTime();
        }
      }
      if (recalculateScheduledDueDate) {
        const scheduledDueDate = costLine.scheduled_due_date
          ? new Date(costLine.scheduled_due_date)
          : null;
        const updatedYear = installationDate.getFullYear() + interval;
        if (
          !scheduledDueDate ||
          scheduledDueDate.getFullYear() !== updatedYear
        ) {
          const updatedDueDate = new Date(installationDate.getTime());
          updatedDueDate.setFullYear(updatedYear);
          updates.scheduled_due_date = updatedDueDate.getTime();
        }
      }
      if (Object.keys(updates).length) {
        await api.costLines.update({
          buildingId: asset.building._id,
          costLineId: costLine._id,
          costLine: updates,
        });
      }
    }
  };

  const handleRecalculateDates = async (assets: WebAssetResponse[]) => {
    setNumTotalAssets(assets.length);
    setNumAssetsUpdated(0);
    setIsBusy(true);
    setErroredAssets([]);
    setErrorMessages([]);
    const erroredAssets: WebAssetResponse[] = [];
    const errorMessages: string[] = [];
    try {
      for (const asset of assets) {
        try {
          await updateDates(asset);
        } catch (e) {
          errorMessages.push(`${e}`);
          erroredAssets.push(asset);
          setErrorMessages(errorMessages);
          setErroredAssets(erroredAssets);
        } finally {
          setNumAssetsUpdated((prev) => prev + 1);
        }
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleRetry = async () => {
    await handleRecalculateDates([...erroredAssets]);
  };

  const isComplete = numAssetsUpdated > 0;
  const isErrored = erroredAssets.length > 0;
  const numAssetsErrored = erroredAssets.length;
  const numUpdated = numAssetsUpdated - numAssetsErrored;

  let content = (
    <>
      <div
        style={{
          padding: "8px 0",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Checkbox
          checked={recalculateAssessedDueDate}
          onChange={(e) => setRecalculateAssessedDueDate(e.target.checked)}
          color="primary"
        />
        <span style={{ paddingLeft: "8px" }}>
          Recalculate Assessed Due Date
        </span>
      </div>
      <div
        style={{
          padding: "8px 0",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Checkbox
          checked={recalculateScheduledDueDate}
          onChange={(e) => setRecalculateScheduledDueDate(e.target.checked)}
          color="primary"
        />
        <span style={{ paddingLeft: "8px" }}>
          Recalculate Scheduled Due Date
        </span>
      </div>
      <div style={{ padding: "8px: 0", color: "red" }}>
        Warning: this will overwrite any previous date values
      </div>
    </>
  );

  let actions = (
    <>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={() => handleRecalculateDates(assets)}
        color="primary"
        variant="contained"
        disableElevation
        disabled={!(recalculateAssessedDueDate || recalculateScheduledDueDate)}
      >
        Recalculate
      </Button>
    </>
  );

  if (isBusy) {
    content = (
      <>
        <DialogContentText id="alert-dialog-description">
          {numTotalAssets === 1
            ? `Updating life cycle dates for ${numTotalAssets} asset cost`
            : `Updating life cycle dates for ${numTotalAssets} asset costs`}
        </DialogContentText>
        <div
          style={{
            padding: "16px 16px 8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <span>{((numAssetsUpdated / numTotalAssets) * 100).toFixed(0)}%</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={(numAssetsUpdated / numTotalAssets) * 100}
        ></LinearProgress>
      </>
    );
  } else if (isErrored) {
    content = (
      <>
        <DialogContentText
          id="alert-dialog-description"
          sx={{ paddingBottom: "8px" }}
        >
          <div style={{ paddingBottom: "16px" }}>
            {numUpdated === 1
              ? `Updated life cycle dates for ${numUpdated} asset cost`
              : `Updated life cycle dates for ${numUpdated} asset costs`}
          </div>
          <div>Errors</div>
        </DialogContentText>
        <Divider />
        <List sx={{ maxHeight: 200, overflowY: "auto" }}>
          {erroredAssets.map((asset, index) => (
            <ListItem key={index}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{`${asset.name}`}</span>
                <span
                  style={{ color: "red" }}
                >{`${errorMessages[index]}`}</span>
              </div>
            </ListItem>
          ))}
        </List>
      </>
    );
    actions = (
      <>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleRetry}
          color="primary"
          variant="contained"
          disableElevation
        >
          Retry
        </Button>
      </>
    );
  } else if (isComplete) {
    content = (
      <>
        <DialogContentText id="alert-dialog-description">
          {numUpdated === 1
            ? `Updated life cycle dates for ${numUpdated} asset cost`
            : `Updated life cycle dates for ${numUpdated} asset costs`}
        </DialogContentText>
        <div
          style={{
            padding: "16px 16px 8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <span>100%</span>
        </div>
        <LinearProgress variant="determinate" value={100}></LinearProgress>
      </>
    );
    actions = (
      <>
        <Button onClick={onClose}>Close</Button>
      </>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Recalculate Life Cycle Dates
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
      {!isBusy && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

export default RecalculateLifeCycleDatesDialog;
